import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UsersRound, Wand2 } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Badge } from '@/components/common/Badge';
import { Modal } from '@/components/common/Modal';
import { SelectField, TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { useWorkingContext } from '@/hooks/useWorkingContext';
import { gradeOfferingsApi } from '@/services/api/endpoints/academic';
import { feeAssignmentsApi, feeStructuresApi, invoicesApi } from '@/services/api/endpoints/finance';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { AssignmentStatus, AutoAssignResult, FeeAssignment } from '@/types/finance';
import type { UUID } from '@/types/domain';
import { formatDate, orDash } from '@/utils/format';
import { Money } from './shared';
import { StudentPicker } from './StudentPicker';

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Which fees each student owes.
 *
 * <p>Assignments hang off an enrolment rather than a student, so billing raised
 * this year stays attached to this year's class after a promotion. The automatic
 * run reads the price list — a fee is assigned because its structure is marked
 * mandatory, never because it is special-cased somewhere.
 */
export function FeeAssignmentsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('startDate,desc');
  const context = useWorkingContext();

  const [statusFilter, setStatusFilter] = useState<AssignmentStatus | ''>('');
  const [autoOpen, setAutoOpen] = useState(false);
  const [autoForm, setAutoForm] = useState({ gradeOfferingId: '', startDate: today() });
  const [autoResult, setAutoResult] = useState<AutoAssignResult | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [student, setStudent] = useState<{ id: UUID; name: string; number?: string } | null>(null);
  const [manual, setManual] = useState({ enrollmentId: '', feeStructureId: '', amount: '', startDate: today(), notes: '' });

  const params = {
    ...table.params,
    ...(statusFilter !== '' ? { status: statusFilter } : {}),
  };

  const query = useQuery({
    queryKey: queryKeys.feeAssignments.list(params),
    queryFn: () => feeAssignmentsApi.list(params),
  });

  const offerings = useQuery({
    queryKey: queryKeys.gradeOfferings.list({ picker: true, year: context.academicYearId }),
    queryFn: () =>
      gradeOfferingsApi.list({
        page: 0, size: 200, status: 'ACTIVE',
        ...(context.academicYearId !== null ? { academicYearId: context.academicYearId } : {}),
      }),
    enabled: autoOpen,
  });

  const structures = useQuery({
    queryKey: queryKeys.feeStructures.list({ picker: true, year: context.academicYearId }),
    queryFn: () =>
      feeStructuresApi.list({
        page: 0, size: 200, status: 'ACTIVE',
        ...(context.academicYearId !== null ? { academicYearId: context.academicYearId } : {}),
      }),
    enabled: manualOpen,
  });

  /** The student's invoices name their enrolment, which is what an assignment attaches to. */
  const enrolments = useQuery({
    queryKey: queryKeys.invoices.list({ forEnrolment: student?.id }),
    queryFn: () => invoicesApi.list({ page: 0, size: 5, studentId: student?.id }),
    enabled: manualOpen && student !== null,
  });

  const autoAssign = useMutation({
    mutationFn: () =>
      feeAssignmentsApi.autoAssign({
        ...(autoForm.gradeOfferingId !== '' ? { gradeOfferingId: autoForm.gradeOfferingId } : {}),
        startDate: autoForm.startDate,
      }),
    onSuccess: (result) => {
      setAutoResult(result);
      toast.success(
        result.assignmentsCreated > 0
          ? `Assigned ${result.assignmentsCreated} fee${result.assignmentsCreated === 1 ? '' : 's'}`
          : 'Nothing new to assign',
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.feeAssignments.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.feeStructures.all });
    },
    onError: (error) => handleApiError(error),
  });

  const assign = useMutation({
    mutationFn: () =>
      feeAssignmentsApi.create({
        enrollmentId: manual.enrollmentId,
        feeStructureId: manual.feeStructureId,
        ...(manual.amount !== '' ? { amount: Number(manual.amount) } : {}),
        startDate: manual.startDate,
        ...(manual.notes !== '' ? { notes: manual.notes } : {}),
      }),
    onSuccess: () => {
      toast.success('Fee assigned');
      setManualOpen(false);
      setStudent(null);
      setManual({ enrollmentId: '', feeStructureId: '', amount: '', startDate: today(), notes: '' });
      void queryClient.invalidateQueries({ queryKey: queryKeys.feeAssignments.all });
    },
    onError: (error) => handleApiError(error),
  });

  const end = useMutation({
    mutationFn: (id: string) => feeAssignmentsApi.changeStatus(id, 'ENDED', today()),
    onSuccess: () => {
      toast.success('Assignment ended — past billing is untouched');
      void queryClient.invalidateQueries({ queryKey: queryKeys.feeAssignments.all });
    },
    onError: (error) => handleApiError(error),
  });

  const chosenStructure = (structures.data?.data ?? []).find((s) => s.id === manual.feeStructureId);

  const columns: Column<FeeAssignment>[] = [
    {
      key: 'student',
      header: 'Student',
      render: (a) => (
        <div>
          <span className="text-ink">{orDash(a.studentName)}</span>
          <span className="block font-mono text-[11px] text-subtle">{orDash(a.studentNumber)}</span>
        </div>
      ),
    },
    {
      key: 'fee',
      header: 'Fee',
      render: (a) => (
        <div>
          <span className="text-ink">{a.feeStructureName}</span>
          <span className="block text-[11px] text-muted">{a.feeCategoryName}</span>
        </div>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      sortKey: 'amount',
      className: 'text-right',
      render: (a) => (
        <div>
          <Money amount={a.amount} bold />
          {a.amountOverridden && (
            <span className="block text-right text-[10.5px] text-warning">differs from the list</span>
          )}
        </div>
      ),
    },
    {
      key: 'window',
      header: 'Billed',
      sortKey: 'startDate',
      render: (a) => (
        <div className="whitespace-nowrap text-[13px]">
          <span className="tabular-nums text-ink">{formatDate(a.startDate)}</span>
          <span className="block text-[11px] text-muted">
            {a.endDate === undefined ? 'ongoing' : `until ${formatDate(a.endDate)}`}
          </span>
        </div>
      ),
    },
    {
      key: 'how',
      header: '',
      render: (a) => (
        <Badge tone={a.assignmentType === 'AUTOMATIC' ? 'neutral' : 'info'}>
          {a.assignmentType.toLowerCase()}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (a) => (
        <Badge tone={a.status === 'ACTIVE' ? 'success' : a.status === 'SUSPENDED' ? 'warning' : 'neutral'}>
          <span className="h-1.5 w-1.5 bg-current" aria-hidden="true" />
          {a.status.toLowerCase()}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (a) =>
        a.status === 'ACTIVE' && can(PERMISSIONS.FEE_ASSIGNMENT_MANAGE) ? (
          <Button size="sm" variant="ghost" loading={end.isPending} onClick={() => end.mutate(a.id)}>
            End
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      <ListPage
        title="Fee Assignments"
        description="Which fees each student owes. Ending an assignment stops future billing and leaves past invoices alone."
        icon={<UsersRound className="h-5 w-5" aria-hidden="true" />}
        action={
          can(PERMISSIONS.FEE_ASSIGNMENT_MANAGE) ? (
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => { setAutoResult(null); setAutoOpen(true); }}>
                <Wand2 className="h-4 w-4" aria-hidden="true" />
                Assign mandatory fees
              </Button>
              <Button onClick={() => setManualOpen(true)}>Assign a fee</Button>
            </div>
          ) : undefined
        }
        filters={
          <FilterSelect label="Status" value={statusFilter}
                        onChange={(value) => { setStatusFilter(value); table.setPage(0); }}
                        options={[
                          { value: 'ACTIVE', label: 'Active' },
                          { value: 'SUSPENDED', label: 'Suspended' },
                          { value: 'ENDED', label: 'Ended' },
                        ]} />
        }
        pagination={query.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={query.data?.data}
          rowKey={(a) => a.id}
          loading={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle="No fees assigned"
          emptyDescription="Assign the mandatory fees for a class, then add optional ones per student."
          emptyIcon={<UsersRound className="h-6 w-6" aria-hidden="true" />}
        />
      </ListPage>

      <Modal
        open={autoOpen}
        title="Assign mandatory fees"
        description="Gives every child in the class the fees marked as assigned automatically."
        onClose={() => { setAutoOpen(false); setAutoResult(null); }}
        footer={
          <>
            <Button variant="secondary" onClick={() => { setAutoOpen(false); setAutoResult(null); }}>
              Close
            </Button>
            <Button loading={autoAssign.isPending} onClick={() => autoAssign.mutate()}>
              Assign
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <SelectField label="Class" value={autoForm.gradeOfferingId}
                       onChange={(event) => setAutoForm((f) => ({ ...f, gradeOfferingId: event.target.value }))}>
            <option value="">Choose a class…</option>
            {(offerings.data?.data ?? []).map((offering) => (
              <option key={offering.id} value={offering.id}>
                {offering.grade.name} · {offering.campusName ?? 'All campuses'}
              </option>
            ))}
          </SelectField>
          <TextField label="Billed from" type="date" required value={autoForm.startDate}
                     hint="Also the date the price list is read on, and the date the class register is read for."
                     onChange={(event) => setAutoForm((f) => ({ ...f, startDate: event.target.value }))} />

          <p className="text-xs text-muted">
            Safe to run again: a fee already assigned is counted and skipped, never duplicated.
          </p>

          {autoResult !== null && (
            <div className="border-2 border-line bg-surface px-4 py-3">
              <dl className="grid grid-cols-3 gap-x-4 text-[13px]">
                <div><dt className="text-subtle">Students</dt>
                     <dd className="tabular-nums text-ink">{autoResult.enrolmentsProcessed}</dd></div>
                <div><dt className="text-subtle">Assigned</dt>
                     <dd className="tabular-nums text-ink">{autoResult.assignmentsCreated}</dd></div>
                <div><dt className="text-subtle">Already had them</dt>
                     <dd className="tabular-nums text-ink">{autoResult.alreadyAssigned}</dd></div>
              </dl>
              {autoResult.warnings.map((warning) => (
                <p key={warning} className="mt-2 text-xs text-warning">{warning}</p>
              ))}
            </div>
          )}
        </div>
      </Modal>

      <Modal
        open={manualOpen}
        title="Assign a fee"
        description="For optional fees such as transport or hostel."
        onClose={() => { setManualOpen(false); setStudent(null); }}
        footer={
          <>
            <Button variant="secondary" onClick={() => { setManualOpen(false); setStudent(null); }}>
              Cancel
            </Button>
            <Button loading={assign.isPending}
                    disabled={manual.enrollmentId === '' || manual.feeStructureId === ''}
                    onClick={() => assign.mutate()}>
              Assign
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <StudentPicker value={student} onChange={(chosen) => {
            setStudent(chosen);
            setManual((m) => ({ ...m, enrollmentId: '' }));
          }} />

          {student !== null && (
            <SelectField label="Enrolment" required value={manual.enrollmentId}
                         hint="The year and class this fee belongs to."
                         onChange={(event) => setManual((m) => ({ ...m, enrollmentId: event.target.value }))}>
              <option value="">Choose…</option>
              {[...new Map((enrolments.data?.data ?? []).map((invoice) => [
                invoice.enrollmentId,
                `${invoice.gradeName ?? 'Class'}${invoice.sectionName !== undefined ? ` ${invoice.sectionName}` : ''} · ${invoice.academicYearName ?? ''}`,
              ])).entries()].map(([id, label]) => (
                <option key={id} value={id}>{label}</option>
              ))}
            </SelectField>
          )}

          <SelectField label="Fee" required value={manual.feeStructureId}
                       onChange={(event) => setManual((m) => ({ ...m, feeStructureId: event.target.value }))}>
            <option value="">Choose…</option>
            {(structures.data?.data ?? [])
              .filter((s) => !s.mandatory)
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} — {s.amount} {s.frequency.replaceAll('_', ' ').toLowerCase()}
                </option>
              ))}
          </SelectField>

          <div className="grid gap-4 sm:grid-cols-2">
            <TextField label="Amount" type="number" step="0.01" value={manual.amount}
                       disabled={chosenStructure !== undefined && !chosenStructure.amountOverridable}
                       hint={chosenStructure === undefined
                         ? 'Leave blank to use the price list.'
                         : chosenStructure.amountOverridable
                           ? 'Leave blank to use the price list.'
                           : 'This fee is charged at a fixed amount. Record a discount instead.'}
                       onChange={(event) => setManual((m) => ({ ...m, amount: event.target.value }))} />
            <TextField label="Billed from" type="date" required value={manual.startDate}
                       hint="A child joining the bus in November is not billed for September."
                       onChange={(event) => setManual((m) => ({ ...m, startDate: event.target.value }))} />
          </div>

          <TextField label="Notes" value={manual.notes}
                     onChange={(event) => setManual((m) => ({ ...m, notes: event.target.value }))} />
        </div>
      </Modal>
    </>
  );
}
